import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("pg_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("pg_token");
    localStorage.removeItem("pg_refresh");
  }, []);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch {
      const refresh = localStorage.getItem("pg_refresh");
      if (refresh) {
        try {
          const res = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: refresh });
          setToken(res.data.access_token);
          localStorage.setItem("pg_token", res.data.access_token);
          localStorage.setItem("pg_refresh", res.data.refresh_token);
          setUser(res.data.user);
        } catch {
          logout();
        }
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const emailNorm = typeof email === "string" ? email.trim().toLowerCase() : email;
    const res = await axios.post(`${API_URL}/api/auth/login`, { email: emailNorm, password });
    setToken(res.data.access_token);
    setUser(res.data.user);
    localStorage.setItem("pg_token", res.data.access_token);
    localStorage.setItem("pg_refresh", res.data.refresh_token);
    return res.data;
  };

  const register = async (data) => {
    const payload = {
      ...data,
      email: typeof data.email === "string" ? data.email.trim().toLowerCase() : data.email,
      first_name: typeof data.first_name === "string" ? data.first_name.trim() : data.first_name,
      last_name: typeof data.last_name === "string" ? data.last_name.trim() : data.last_name,
      phone: typeof data.phone === "string" ? data.phone.trim() : data.phone ?? "",
    };
    const res = await axios.post(`${API_URL}/api/auth/register`, payload);
    setToken(res.data.access_token);
    setUser(res.data.user);
    localStorage.setItem("pg_token", res.data.access_token);
    localStorage.setItem("pg_refresh", res.data.refresh_token);
    return res.data;
  };

  const updateUser = (data) => setUser((prev) => ({ ...prev, ...data }));

  const isAuthenticated = !!user;
  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, register, updateUser, isAuthenticated, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
